import React, { useState, useEffect, useMemo } from "react";
import "./GrillaEstadisticaReq.css";
import { getEstadisticaRequerimientos } from "../../api/transactionDocument";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faFileExcel, faSpinner, faMagnifyingGlass, faTableCells } from "@fortawesome/free-solid-svg-icons";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const getCurrentYear = () => new Date().getFullYear();

const formatFecha = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
};

const normalizeStr = (str) =>
  String(str).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const matchesFilter = (row, filter) => {
  if (!filter.trim()) return true;
  const fields = [
    row.title || "",
    row.tDocCodFor || "",
    String(row.tDocNroFor ?? ""),
    row.userName || "",
    row.tDocAbmReqSectorCodsem || "",
    row.tDocAbmReqSectorDescrp || "",
    row.usrAut || "",
    row.state || "",
    row.tDocAbmWarehouseDeposi || "",
    row.tDocAbmWarehouseDescrp || "",
  ].map(normalizeStr).join(" ");

  const exactMatch = filter.match(/^"(.+)"$/);
  if (exactMatch) {
    return fields.includes(normalizeStr(exactMatch[1]));
  }

  const words = normalizeStr(filter).trim().split(/\s+/);
  return words.every((w) => fields.includes(w));
};

const INITIAL_COLUMNS = [
  { property: "title",        title: "Título",         visible: true,  flex: 2 },
  { property: "tDocCodFor",   title: "Código",         visible: true,  width: 70 },
  { property: "tDocNroFor",   title: "Número",         visible: true,  width: 70 },
  { property: "fchMov",       title: "Fch. Mov.",      visible: true,  width: 100 },
  { property: "userName",     title: "Usuario",        visible: true,  width: 130 },
  { property: "deliveryDate", title: "Fecha entrega",  visible: false, width: 110 },
  { property: "usrAut",       title: "Usuario aut.",   visible: false, width: 120 },
  { property: "deposito",     title: "Depósito",       visible: false, width: 160 },
  { property: "state",        title: "Estado",         visible: false, width: 100 },
  { property: "media",        title: "Media",          visible: true,  width: 70 },
  { property: "alta",         title: "Alta",           visible: true,  width: 70 },
  { property: "urgente",      title: "Urgente",        visible: true,  width: 70 },
];

const GrillaEstadisticaReq = ({ setGridToShow }) => {
  const [datos, setDatos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [desdeAnio, setDesdeAnio] = useState(getCurrentYear());
  const [desdeMes, setDesdeMes] = useState(1);
  const [hastaAnio, setHastaAnio] = useState(getCurrentYear());
  const [hastaMes, setHastaMes] = useState(12);
  const [filter, setFilter] = useState("");
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [showSelectColumns, setShowSelectColumns] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const desdeDate = `${desdeAnio}-${String(desdeMes).padStart(2, "0")}-01`;
      const hastaDate = `${hastaAnio}-${String(hastaMes).padStart(2, "0")}-${new Date(hastaAnio, hastaMes, 0).getDate()}`;
      const resp = await getEstadisticaRequerimientos(desdeDate, hastaDate);
      setDatos(resp.data.data || []);
    } catch (error) {
      console.error("Error al cargar estadística de requerimientos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const datosFiltrados = useMemo(() => {
    if (!filter.trim()) return datos;
    return datos.filter((r) => matchesFilter(r, filter));
  }, [datos, filter]);

  const grupos = useMemo(() => {
    const gMap = {};
    for (const row of datosFiltrados) {
      const d = new Date(row.fecAlt);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const mesKey = `${year}-${String(month).padStart(2, "0")}`;
      const sectorKey = row.tDocAbmReqSectorCodsem || "__sin_sector__";

      if (!gMap[mesKey]) {
        gMap[mesKey] = {
          year, month,
          label: `${MESES[month - 1]} ${year}`,
          sectores: {},
          totalMes: 0, mediaMes: 0, altaMes: 0, urgenteMes: 0,
        };
      }
      if (!gMap[mesKey].sectores[sectorKey]) {
        gMap[mesKey].sectores[sectorKey] = {
          sector: row.tDocAbmReqSectorCodsem || "-",
          sectorDescrp: row.tDocAbmReqSectorDescrp || "",
          rows: [],
          totalSector: 0, mediaSector: 0, altaSector: 0, urgenteSector: 0,
        };
      }

      gMap[mesKey].sectores[sectorKey].rows.push(row);
      gMap[mesKey].sectores[sectorKey].totalSector += 1;
      if (row.priority === 0) gMap[mesKey].sectores[sectorKey].mediaSector += 1;
      if (row.priority === 1) gMap[mesKey].sectores[sectorKey].altaSector += 1;
      if (row.priority === 2) gMap[mesKey].sectores[sectorKey].urgenteSector += 1;

      gMap[mesKey].totalMes += 1;
      if (row.priority === 0) gMap[mesKey].mediaMes += 1;
      if (row.priority === 1) gMap[mesKey].altaMes += 1;
      if (row.priority === 2) gMap[mesKey].urgenteMes += 1;
    }

    return Object.values(gMap)
      .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
      .map((g) => ({
        ...g,
        sectores: Object.values(g.sectores).sort((a, b) => a.sector.localeCompare(b.sector)),
      }));
  }, [datosFiltrados]);

  const colStyle = (property) => {
    const col = columns.find((c) => c.property === property);
    return col && !col.visible ? { display: "none" } : {};
  };

  const handleChangeColumnsVisible = (e, i) => {
    setColumns((prev) => prev.map((c, idx) => idx === i ? { ...c, visible: e.target.checked } : c));
  };

  const handleCheckAllColumns = () => {
    const allVisible = columns.every((c) => c.visible);
    setColumns((prev) => prev.map((c) => ({ ...c, visible: !allVisible })));
  };

  const handleExportar = () => {
    try {
      const XLSX = require("xlsx");
      const filas = [["Mes", "Sector", "Título", "Código", "Número", "Fch. Mov.", "Usuario", "Fecha entrega", "Usuario aut.", "Depósito", "Estado", "Media", "Alta", "Urgente"]];
      for (const grupo of grupos) {
        for (const sec of grupo.sectores) {
          for (const row of sec.rows) {
            filas.push([
              grupo.label,
              sec.sector !== "-" ? `${sec.sector} (${sec.sectorDescrp})` : "-",
              row.title || "-",
              row.tDocCodFor || "-",
              row.tDocNroFor ?? "-",
              formatFecha(row.fchMov),
              row.userName || "-",
              formatFecha(row.deliveryDate),
              row.usrAut || "-",
              row.tDocAbmWarehouseDeposi ? `${row.tDocAbmWarehouseDeposi}${row.tDocAbmWarehouseDescrp ? ` (${row.tDocAbmWarehouseDescrp})` : ""}` : "-",
              row.state || "-",
              row.priority === 0 ? 1 : "",
              row.priority === 1 ? 1 : "",
              row.priority === 2 ? 1 : "",
            ]);
          }
          filas.push([`  Subtotal ${sec.sector}`, "", "", "", "", "", "", "", "", "", "", sec.mediaSector, sec.altaSector, sec.urgenteSector]);
          filas.push([]);
        }
        filas.push([`TOTAL ${grupo.label}`, "", "", "", "", "", "", "", "", "", "", grupo.mediaMes, grupo.altaMes, grupo.urgenteMes]);
        filas.push([]);
      }
      filas.push(["TOTAL GENERAL", "", "", "", "", "", "", "", "", "", "", grupos.reduce((s, g) => s + g.mediaMes, 0), grupos.reduce((s, g) => s + g.altaMes, 0), grupos.reduce((s, g) => s + g.urgenteMes, 0)]);
      const ws = XLSX.utils.aoa_to_sheet(filas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Estadistica");
      XLSX.writeFile(wb, "EstadisticaRequerimientos.xlsx");
    } catch (e) {
      console.error("Error al exportar:", e);
    }
  };

  const anios = [];
  for (let y = 2020; y <= getCurrentYear() + 1; y++) anios.push(y);

  return (
    <div className="grillaInformeDiv">
      <div className="utilidades-reporte">
        <button className="btnVolver" onClick={() => setGridToShow("")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Volver
        </button>
        <h2 className="titulo-reporte">Estadística de Requerimientos</h2>
        <div className="utilidades-reporte-right">
          <button className="btnSelectColumnas" onClick={() => setShowSelectColumns(true)}>
            <FontAwesomeIcon icon={faTableCells} /> Columnas
          </button>
          <button className="btnExcel" onClick={handleExportar}>
            <FontAwesomeIcon icon={faFileExcel} /> Exportar Excel
          </button>
        </div>
      </div>

      <div className="filtros-reporte">
        <div className="filtro-grupo filtro-busqueda">
          <label>Buscar</label>
          <div className="search-reporte-container">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-reporte-icon" />
            <input
              className="search-reporte-input"
              placeholder='Buscar... puede encerrar entre comillas dobles para "coincidencia exacta"'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>
        <div className="filtro-grupo">
          <label>Desde</label>
          <div className="filtro-fecha">
            <select value={desdeMes} onChange={(e) => setDesdeMes(Number(e.target.value))}>
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={desdeAnio} onChange={(e) => setDesdeAnio(Number(e.target.value))}>
              {anios.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div className="filtro-grupo">
          <label>Hasta</label>
          <div className="filtro-fecha">
            <select value={hastaMes} onChange={(e) => setHastaMes(Number(e.target.value))}>
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={hastaAnio} onChange={(e) => setHastaAnio(Number(e.target.value))}>
              {anios.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <button className="btnBuscar" onClick={fetchData} disabled={isLoading}>
          {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Buscar"}
        </button>
      </div>

      {isLoading ? (
        <div className="loading-reporte">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Cargando datos...</p>
        </div>
      ) : datos.length === 0 ? (
        <div className="sin-datos">No hay datos para el período seleccionado.</div>
      ) : grupos.length === 0 ? (
        <div className="sin-datos">No se encontraron resultados para la búsqueda.</div>
      ) : (
        <div className="tabla-wrapper">
          <div className="similTable">
            <div className="columnsHeader">
              <div className="columnHeader" style={{ width: 90 }}>Mes</div>
              <div className="columnHeader" style={{ ...colStyle("title"), flex: 2 }}>Título</div>
              <div className="columnHeader" style={{ ...colStyle("tDocCodFor"), width: 70 }}>Código</div>
              <div className="columnHeader" style={{ ...colStyle("tDocNroFor"), width: 70 }}>Número</div>
              <div className="columnHeader" style={{ ...colStyle("fchMov"), width: 100 }}>Fch. Mov.</div>
              <div className="columnHeader" style={{ ...colStyle("userName"), width: 130 }}>Usuario</div>
              <div className="columnHeader" style={{ ...colStyle("deliveryDate"), width: 110 }}>Fecha entrega</div>
              <div className="columnHeader" style={{ ...colStyle("usrAut"), width: 120 }}>Usuario aut.</div>
              <div className="columnHeader" style={{ ...colStyle("deposito"), width: 160 }}>Depósito</div>
              <div className="columnHeader" style={{ ...colStyle("state"), width: 100 }}>Estado</div>
              <div className="columnHeader prioridad-media" style={{ ...colStyle("media"), width: 70 }}>Media</div>
              <div className="columnHeader prioridad-alta" style={{ ...colStyle("alta"), width: 70 }}>Alta</div>
              <div className="columnHeader prioridad-urgente" style={{ ...colStyle("urgente"), width: 70 }}>Urgente</div>
            </div>

            <div className="rowsData">
              {grupos.map((grupo) => (
                <React.Fragment key={`${grupo.year}-${grupo.month}`}>
                  {grupo.sectores.map((sec, si) => (
                    <React.Fragment key={sec.sector}>
                      <div className="rowSectorHeader">
                        <div style={{ width: 90 }}>{grupo.label}</div>
                        <div style={{ flex: 2 }} className="sector-label">
                          {sec.sector !== "-" ? `${sec.sector} (${sec.sectorDescrp})` : "Sin sector"}
                        </div>
                        <div style={{ ...colStyle("tDocCodFor"), width: 70 }}></div>
                        <div style={{ ...colStyle("tDocNroFor"), width: 70 }}></div>
                        <div style={{ ...colStyle("fchMov"), width: 100 }}></div>
                        <div style={{ ...colStyle("userName"), width: 130 }}></div>
                        <div style={{ ...colStyle("deliveryDate"), width: 110 }}></div>
                        <div style={{ ...colStyle("usrAut"), width: 120 }}></div>
                        <div style={{ ...colStyle("deposito"), width: 160 }}></div>
                        <div style={{ ...colStyle("state"), width: 100 }}></div>
                        <div style={{ ...colStyle("media"), width: 70 }}></div>
                        <div style={{ ...colStyle("alta"), width: 70 }}></div>
                        <div style={{ ...colStyle("urgente"), width: 70 }}></div>
                      </div>
                      {sec.rows.map((row, i) => (
                        <div className="rowData" key={i}>
                          <div className="rowDataChild" style={{ width: 90 }}></div>
                          <div className="rowDataChild justify-start" style={{ ...colStyle("title"), flex: 2 }}>
                            {row.title || "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("tDocCodFor"), width: 70 }}>
                            {row.tDocCodFor || "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("tDocNroFor"), width: 70 }}>
                            {row.tDocNroFor ?? "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("fchMov"), width: 100 }}>
                            {formatFecha(row.fchMov)}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("userName"), width: 130 }}>
                            {row.userName || "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("deliveryDate"), width: 110 }}>
                            {formatFecha(row.deliveryDate)}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("usrAut"), width: 120 }}>
                            {row.usrAut || "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("deposito"), width: 160 }}>
                            {row.tDocAbmWarehouseDeposi
                              ? `${row.tDocAbmWarehouseDeposi}${row.tDocAbmWarehouseDescrp ? ` (${row.tDocAbmWarehouseDescrp})` : ""}`
                              : "-"}
                          </div>
                          <div className="rowDataChild" style={{ ...colStyle("state"), width: 100 }}>
                            {row.state || "-"}
                          </div>
                          <div className="rowDataChild celda-media" style={{ ...colStyle("media"), width: 70 }}>
                            {row.priority === 0 ? 1 : ""}
                          </div>
                          <div className="rowDataChild celda-alta" style={{ ...colStyle("alta"), width: 70 }}>
                            {row.priority === 1 ? 1 : ""}
                          </div>
                          <div className="rowDataChild celda-urgente" style={{ ...colStyle("urgente"), width: 70 }}>
                            {row.priority === 2 ? 1 : ""}
                          </div>
                        </div>
                      ))}
                      <div className="rowSubtotalSector">
                        <div style={{ width: 90 }}></div>
                        <div style={{ flex: 2 }}>Subtotal {sec.sector}: {sec.totalSector}</div>
                        <div style={{ ...colStyle("tDocCodFor"), width: 70 }}></div>
                        <div style={{ ...colStyle("tDocNroFor"), width: 70 }}></div>
                        <div style={{ ...colStyle("fchMov"), width: 100 }}></div>
                        <div style={{ ...colStyle("userName"), width: 130 }}></div>
                        <div style={{ ...colStyle("deliveryDate"), width: 110 }}></div>
                        <div style={{ ...colStyle("usrAut"), width: 120 }}></div>
                        <div style={{ ...colStyle("deposito"), width: 160 }}></div>
                        <div style={{ ...colStyle("state"), width: 100 }}></div>
                        <div className="subtotal-valor celda-media" style={{ ...colStyle("media"), width: 70 }}>{sec.mediaSector}</div>
                        <div className="subtotal-valor celda-alta" style={{ ...colStyle("alta"), width: 70 }}>{sec.altaSector}</div>
                        <div className="subtotal-valor celda-urgente" style={{ ...colStyle("urgente"), width: 70 }}>{sec.urgenteSector}</div>
                      </div>
                    </React.Fragment>
                  ))}
                  <div className="rowSubtotal">
                    <div style={{ width: 170 }}>Total {grupo.label}: <span className="total-mes-numero">{grupo.totalMes}</span></div>
                    <div style={{ ...colStyle("title"), flex: 2 }}></div>
                    <div style={{ ...colStyle("tDocCodFor"), width: 70 }}></div>
                    <div style={{ ...colStyle("tDocNroFor"), width: 70 }}></div>
                    <div style={{ ...colStyle("fchMov"), width: 100 }}></div>
                    <div style={{ ...colStyle("userName"), width: 130 }}></div>
                    <div style={{ ...colStyle("deliveryDate"), width: 110 }}></div>
                    <div style={{ ...colStyle("usrAut"), width: 120 }}></div>
                    <div style={{ ...colStyle("deposito"), width: 160 }}></div>
                    <div style={{ ...colStyle("state"), width: 100 }}></div>
                    <div className="subtotal-valor celda-media" style={{ ...colStyle("media"), width: 70 }}>{grupo.mediaMes}</div>
                    <div className="subtotal-valor celda-alta" style={{ ...colStyle("alta"), width: 70 }}>{grupo.altaMes}</div>
                    <div className="subtotal-valor celda-urgente" style={{ ...colStyle("urgente"), width: 70 }}>{grupo.urgenteMes}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div className="rowsTotales">
              <div className="rowTotalesChild" style={{ width: 90 }}>
                TOTAL: {grupos.reduce((s, g) => s + g.totalMes, 0)}
              </div>
              <div className="rowTotalesChild" style={{ ...colStyle("title"), flex: 2 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("tDocCodFor"), width: 70 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("tDocNroFor"), width: 70 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("fchMov"), width: 100 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("userName"), width: 130 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("deliveryDate"), width: 110 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("usrAut"), width: 120 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("deposito"), width: 160 }}></div>
              <div className="rowTotalesChild" style={{ ...colStyle("state"), width: 100 }}></div>
              <div className="rowTotalesChild celda-media" style={{ ...colStyle("media"), width: 70 }}>
                {grupos.reduce((s, g) => s + g.mediaMes, 0)}
              </div>
              <div className="rowTotalesChild celda-alta" style={{ ...colStyle("alta"), width: 70 }}>
                {grupos.reduce((s, g) => s + g.altaMes, 0)}
              </div>
              <div className="rowTotalesChild celda-urgente" style={{ ...colStyle("urgente"), width: 70 }}>
                {grupos.reduce((s, g) => s + g.urgenteMes, 0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSelectColumns && (
        <div className="modal-columnas-overlay" onClick={() => setShowSelectColumns(false)}>
          <div className="modal-columnas-div" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-columnas-header">Seleccionar Columnas</h3>
            <button className="modal-columnas-close" onClick={() => setShowSelectColumns(false)}>X</button>
            {columns.map((c, i) => (
              <div key={c.property} className="selectable-column-div">
                <label>
                  <input
                    type="checkbox"
                    checked={c.visible}
                    onChange={(e) => handleChangeColumnsVisible(e, i)}
                  />
                  <span className="custom-checkbox" />
                  {c.title}
                </label>
              </div>
            ))}
            <div className="modal-columnas-footer">
              <button className="btn-tildar-todas" onClick={handleCheckAllColumns}>
                {columns.every((c) => c.visible) ? "Destildar todas" : "Tildar todas"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrillaEstadisticaReq;
